import { CheckCircle2, Mail, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import { ResponsiveTable } from '../../components/admin/responsive';
import { api } from '../../services/api';

function fmtData(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}
function fmtReais(cents) {
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}
function initials(nome) {
  return (nome ?? '?')
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

function Tooltip({ title, children }) {
  const [isVisible, setIsVisible] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
      </div>
      {isVisible && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#0f172a',
            color: '#fff',
            padding: '6px 10px',
            borderRadius: 6,
            fontSize: 10,
            whiteSpace: 'nowrap',
            zIndex: 10,
            marginBottom: 4,
          }}
        >
          {title}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderTop: '4px solid #0f172a',
            }}
          />
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    CONFIRMED: {
      label: 'Confirmado',
      bg: '#dcfce7',
      color: '#166534',
      border: '#bbf7d0',
    },
    PENDING: {
      label: 'Pendente',
      bg: '#fef9c3',
      color: '#854d0e',
      border: '#fde68a',
    },
    CANCELLED: {
      label: 'Cancelado',
      bg: '#fee2e2',
      color: '#991b1b',
      border: '#fecaca',
    },
  };
  const s = map[status] ?? {
    label: status,
    bg: '#f1f5f9',
    color: '#64748b',
    border: '#e2e8f0',
  };
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 900,
        letterSpacing: '.06em',
        padding: '3px 9px',
        borderRadius: 99,
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
      }}
    >
      {s.label}
    </span>
  );
}

function Avatar({ nome, size = 32 }) {
  const colors = [
    '#7c5cbf',
    '#ff2d6c',
    '#0369a1',
    '#059669',
    '#d97706',
    '#be185d',
  ];
  const idx = (nome?.charCodeAt(0) ?? 0) % colors.length;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: colors[idx] + '22',
        border: `1.5px solid ${colors[idx]}44`,
        color: colors[idx],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.33,
        fontWeight: 900,
        flexShrink: 0,
      }}
    >
      {initials(nome)}
    </div>
  );
}

function badge(bg, color, border) {
  return {
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: '.06em',
    padding: '3px 9px',
    borderRadius: 99,
    background: bg,
    color,
    border: `1px solid ${border}`,
  };
}

export default function InscricoesPorEventoEspecial() {
  const { eventoId } = useParams();
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [enviando, setEnviando] = useState(null);
  const [sincronizando, setSincronizando] = useState(null);
  const [sincronizandoBulk, setSincronizandoBulk] = useState(false);
  const [modalExportacao, setModalExportacao] = useState(null);
  const [ordemFiltro, setOrdemFiltro] = useState('alfabetico');

  function recarregar() {
    if (!eventoId) return;
    api
      .get(`/admin/eventos-especiais/${eventoId}/inscricoes`)
      .then(({ data }) => setDados(data))
      .catch(() => {});
  }

  useEffect(() => {
    if (!eventoId) return;
    setCarregando(true);
    setDados(null);
    api
      .get(`/admin/eventos-especiais/${eventoId}/inscricoes`)
      .then(({ data }) => setDados(data))
      .catch(() =>
        setFeedback({ msg: 'Erro ao carregar inscrições.', tipo: 'erro' }),
      )
      .finally(() => setCarregando(false));
  }, [eventoId]);

  function mostrarFeedback(msg, tipo = 'ok') {
    setFeedback({ msg, tipo });
    setTimeout(() => setFeedback(null), 5000);
  }

  async function enviarComprovante(inscricaoId, nome, email) {
    if (!confirm(`Enviar comprovante para ${nome} (${email})?`)) return;
    setEnviando(inscricaoId);
    try {
      const { data } = await api.post(
        `/admin/eventos-especiais/inscricoes/${inscricaoId}/comprovante`,
      );
      mostrarFeedback(data.mensagem);
    } catch (err) {
      mostrarFeedback(
        err.response?.data?.error || 'Erro ao enviar comprovante.',
        'erro',
      );
    } finally {
      setEnviando(null);
    }
  }

  async function sincronizarInscricao(inscricaoId, nome) {
    setSincronizando(inscricaoId);
    try {
      const { data } = await api.post(
        `/admin/eventos-especiais/inscricoes/${inscricaoId}/sincronizar`,
      );
      if (data.atualizado) {
        mostrarFeedback(data.mensagem);
        recarregar();
      } else {
        mostrarFeedback(`${nome}: ${data.mensagem}`, 'aviso');
      }
    } catch (err) {
      mostrarFeedback(
        err.response?.data?.error || 'Erro ao consultar Asaas.',
        'erro',
      );
    } finally {
      setSincronizando(null);
    }
  }

  async function confirmarInscricao(inscricaoId, nome) {
    if (
      !confirm(
        `Confirmar manualmente a compra de ${nome}? Isso marcará como paga e enviará o comprovante por email.`,
      )
    )
      return;
    try {
      const { data } = await api.post(
        `/admin/eventos-especiais/inscricoes/${inscricaoId}/confirmar`,
      );
      mostrarFeedback(data.mensagem);
      recarregar();
    } catch (err) {
      mostrarFeedback(
        err.response?.data?.error || 'Erro ao confirmar compra.',
        'erro',
      );
    }
  }

  async function sincronizarTodosPendentes() {
    if (
      !confirm(
        'Sincronizar status de TODOS os pagamentos pendentes com Asaas? Isso pode levar alguns segundos.',
      )
    )
      return;
    setSincronizandoBulk(true);
    try {
      const { data } = await api.post(
        `/admin/eventos-especiais/${eventoId}/sincronizar-pendentes`,
      );
      mostrarFeedback(data.mensagem);
      recarregar();
    } catch (err) {
      mostrarFeedback(
        err.response?.data?.error || 'Erro ao sincronizar pagamentos.',
        'erro',
      );
    } finally {
      setSincronizandoBulk(false);
    }
  }

  async function abrirExportacao() {
    if (!eventoId) {
      mostrarFeedback('Evento não encontrado', 'erro');
      return;
    }
    try {
      // Usa o endpoint que já existe e funciona
      const { data } = await api.get(`/admin/eventos-especiais/${eventoId}/inscricoes`);

      // Apenas CONFIRMADOS (pagos)
      const inscricoes = data.inscricoes
        .filter((i) => i.status === 'CONFIRMED')
        .map((i) => ({
          nomeCompleto: i.nome,
          cpf: i.cpf || "—",
          email: i.email,
          whatsapp: i.whatsapp || "—",
          status: i.status,
        }));

      setModalExportacao({
        evento: data.evento,
        total: inscricoes.length,
        inscricoes,
      });
      setOrdemFiltro('alfabetico');
    } catch (err) {
      console.error('Erro:', err);
      mostrarFeedback('Erro ao carregar inscrições: ' + err.message, 'erro');
    }
  }

  function ordenarInscricoes(inscricoes, ordem) {
    const cópia = [...inscricoes];
    switch(ordem) {
      case 'alfabetico':
        return cópia.sort((a, b) => a.nomeCompleto.localeCompare(b.nomeCompleto, 'pt-BR'));
      case 'reverso':
        return cópia.sort((a, b) => b.nomeCompleto.localeCompare(a.nomeCompleto, 'pt-BR'));
      case 'recente':
        return cópia.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case 'antigo':
        return cópia.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      default:
        return cópia;
    }
  }

  function exportarComoCsv() {
    if (!modalExportacao) return;
    const ordenadas = ordenarInscricoes(modalExportacao.inscricoes, ordemFiltro);
    const lines = [
      'Nome Completo\tCPF\tEmail\tWhatsApp\tStatus',
      ...ordenadas.map((i) =>
        `${i.nomeCompleto}\t${i.cpf}\t${i.email}\t${i.whatsapp}\t${i.status}`
      )
    ];
    const csv = lines.join('\n');
    const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const blob = new Blob([bom, csv], { type: 'text/tab-separated-values;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `inscricoes-${modalExportacao.evento.title.replace(/\s+/g, '_')}.tsv`;
    link.click();
  }

  function copiarParaAreaTransferencia() {
    if (!modalExportacao) return;
    const ordenadas = ordenarInscricoes(modalExportacao.inscricoes, ordemFiltro);
    const texto = ordenadas
      .map((i) => `${i.nomeCompleto} | ${i.cpf} | ${i.email}`)
      .join('\n');
    navigator.clipboard.writeText(texto);
    mostrarFeedback('Copiado para a área de transferência!');
  }

  const inscricoes = dados?.inscricoes ?? [];
  const totalInscritas = inscricoes.length;
  const totalConfirmadas = inscricoes.filter(
    (i) => i.status === 'CONFIRMED',
  ).length;
  const receitaTotal = inscricoes
    .filter((i) => i.status === 'CONFIRMED')
    .reduce((a, i) => a + i.amountInCents, 0);
  const ocupacao = dados?.evento?.capacity
    ? Math.round((totalInscritas / dados.evento.capacity) * 100)
    : null;

  return (
    <AdminLayout>
      {modalExportacao && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              width: '100%',
              maxWidth: 800,
              maxHeight: '80vh',
              boxShadow: '0 20px 60px rgba(0,0,0,.2)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div style={{ background: '#7c3aed', padding: '20px 24px' }}>
              <div style={{ fontWeight: 900, fontSize: 18, color: '#fff', marginBottom: 4 }}>
                {modalExportacao.evento.title}
              </div>
              <div style={{ fontSize: 13, color: '#ddd6fe' }}>
                Total de inscrições: {modalExportacao.total}
              </div>
            </div>

            <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', background: '#fafafa' }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginRight: 8 }}>
                Ordenar por:
              </label>
              <select
                value={ordemFiltro}
                onChange={(e) => setOrdemFiltro(e.target.value)}
                style={{
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid #e2e8f0',
                  fontWeight: 500,
                  fontSize: 13,
                  cursor: 'pointer',
                  background: '#fff',
                }}
              >
                <option value="alfabetico">A-Z (Alfabético)</option>
                <option value="reverso">Z-A (Reverso)</option>
                <option value="recente">Mais Recente</option>
                <option value="antigo">Mais Antigo</option>
              </select>
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
              <textarea
                readOnly
                value={ordenarInscricoes(modalExportacao.inscricoes, ordemFiltro)
                  .map((i) => `${i.nomeCompleto} | ${i.cpf} | ${i.email}`)
                  .join('\n')}
                style={{
                  width: '100%',
                  height: '100%',
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                  padding: '12px',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  color: '#0f172a',
                  resize: 'none',
                  fontWeight: 600,
                }}
              />
            </div>

            <div style={{ padding: '16px 24px', background: '#fafafa', display: 'flex', gap: 10 }}>
              <button
                onClick={() => setModalExportacao(null)}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: 10,
                  border: '1.5px solid #e2e8f0',
                  background: '#fff',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                  color: '#64748b',
                }}
              >
                Fechar
              </button>
              <button
                onClick={copiarParaAreaTransferencia}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: 10,
                  border: '1.5px solid #ddd6fe',
                  background: '#f5f3ff',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                  color: '#7c3aed',
                }}
              >
                📋 Copiar
              </button>
              <button
                onClick={exportarComoCsv}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: 10,
                  background: '#7c3aed',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                  color: '#fff',
                  border: 'none',
                }}
              >
                ↓ Baixar CSV
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 24,
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <button
            onClick={() => navigate('/admin/eventos-especiais')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#94a3b8',
              fontSize: 13,
              padding: 0,
              marginBottom: 6,
            }}
          >
            ← Voltar para Lily Dark
          </button>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 900,
              color: '#0f172a',
              margin: 0,
            }}
          >
            {dados?.evento?.title ?? 'Inscrições do Evento'}
          </h1>
          {dados && (
            <p style={{ fontSize: 13, color: '#94a3b8', margin: '4px 0 0' }}>
              {fmtData(dados.evento.startDate)} · {totalInscritas} inscrita(s)
              {dados.evento.capacity
                ? ` · ${dados.evento.capacity - totalInscritas} vaga(s) livre(s)`
                : ''}
            </p>
          )}
        </div>
      </div>

      {feedback && (
        <div
          style={{
            padding: '12px 16px',
            borderRadius: 10,
            marginBottom: 20,
            fontSize: 13,
            fontWeight: 700,
            background:
              feedback.tipo === 'erro'
                ? '#fee2e2'
                : feedback.tipo === 'aviso'
                  ? '#fef3c7'
                  : '#dcfce7',
            color:
              feedback.tipo === 'erro'
                ? '#991b1b'
                : feedback.tipo === 'aviso'
                  ? '#92400e'
                  : '#166534',
          }}
        >
          {feedback.msg}
        </div>
      )}

      {dados && (
        <div
          style={{
            display: 'flex',
            gap: 12,
            marginBottom: 24,
            flexWrap: 'wrap',
          }}
        >
          {[
            {
              label: 'Total inscritas',
              value: totalInscritas,
              color: '#0f172a',
            },
            { label: 'Confirmadas', value: totalConfirmadas, color: '#166534' },
            {
              label: 'Pendentes',
              value: totalInscritas - totalConfirmadas,
              color: '#854d0e',
            },
            {
              label: 'Receita confirmada',
              value: fmtReais(receitaTotal),
              color: '#7c3aed',
            },
            ...(ocupacao !== null
              ? [{ label: 'Ocupação', value: `${ocupacao}%`, color: '#0369a1' }]
              : []),
          ].map((m) => (
            <div
              key={m.label}
              style={{
                background: '#fff',
                borderRadius: 12,
                padding: '14px 20px',
                border: '1px solid #f1f5f9',
                flex: 1,
                minWidth: 140,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: '#94a3b8',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '.06em',
                  marginBottom: 4,
                }}
              >
                {m.label}
              </div>
              <div style={{ fontSize: 22, fontWeight: 900, color: m.color }}>
                {m.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {carregando && (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>
          Carregando inscrições...
        </div>
      )}

      {!carregando && dados && (
        <>
          {/* Ações em massa */}
          <div style={{ marginBottom: 20, display: 'flex', gap: 12 }}>
            <button
              onClick={abrirExportacao}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 16px',
                background: '#f5f3ff',
                color: '#7c3aed',
                border: '1px solid #ddd6fe',
                borderRadius: 10,
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              ↓ Exportar Inscrições
            </button>
            {dados.inscricoes.filter((i) => i.status === 'PENDING').length >
              0 && (
              <button
                onClick={sincronizarTodosPendentes}
                disabled={sincronizandoBulk}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 16px',
                  background: '#dbeafe',
                  color: '#1e40af',
                  border: '1px solid #bfdbfe',
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: sincronizandoBulk ? 'default' : 'pointer',
                  opacity: sincronizandoBulk ? 0.6 : 1,
                }}
              >
                <RefreshCw size={14} />
                {sincronizandoBulk
                  ? 'Sincronizando...'
                  : 'Sincronizar todos os pendentes'}
              </button>
            )}
          </div>
          {dados.inscricoes.filter((i) => i.status === 'PENDING').length >
            0 && (
            <p
              style={{
                fontSize: 11,
                color: '#64748b',
                marginTop: 0,
                marginBottom: 20,
                margin: 0,
              }}
            >
              Verifica status de{' '}
              {dados.inscricoes.filter((i) => i.status === 'PENDING').length}{' '}
              pagamento(s) no Asaas
            </p>
          )}
        </>
      )}

      {!carregando && dados && (
        <ResponsiveTable
          data={inscricoes}
          columns={[
            {
              key: 'nome',
              label: 'Nome',
              render: (_, row) => (
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <Avatar nome={row.nome} />
                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 13,
                        color: '#0f172a',
                      }}
                    >
                      {row.nome}
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>
                      {row.email}
                    </div>
                  </div>
                </div>
              ),
            },
            {
              key: 'cpf',
              label: 'CPF',
            },
            {
              key: 'whatsapp',
              label: 'WhatsApp',
              render: (v) => v || '—',
            },
            {
              key: 'pagamento',
              label: 'Pagamento',
              render: (pag) => {
                if (!pag) return '—';
                return (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 900,
                      letterSpacing: '.06em',
                      padding: '3px 9px',
                      borderRadius: 99,
                      background:
                        pag.billingType === 'PIX' ? '#fef9c3' : '#ede9fe',
                      color: pag.billingType === 'PIX' ? '#854d0e' : '#5b21b6',
                      border:
                        pag.billingType === 'PIX'
                          ? '1px solid #fde68a'
                          : '1px solid #c4b5fd',
                    }}
                  >
                    {pag.billingType === 'PIX' ? 'PIX' : 'Cartão'}
                  </span>
                );
              },
            },
            {
              key: 'amountInCents',
              label: 'Valor',
              render: (v) =>
                (v / 100).toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }),
            },
            {
              key: 'status',
              label: 'Status',
              render: (status) => <StatusBadge status={status} />,
            },
            {
              key: 'actions',
              label: 'Ações',
              render: (_, row) => (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {row.status === 'PENDING' && (
                    <Tooltip title="Confirmar manualmente">
                      <button
                        onClick={() => confirmarInscricao(row.id, row.nome)}
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#166534',
                          background: '#f0fdf4',
                          border: '1px solid #bbf7d0',
                          borderRadius: 99,
                          padding: '5px 12px',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <CheckCircle2 size={12} />
                        Confirmar
                      </button>
                    </Tooltip>
                  )}
                  {row.status === 'PENDING' &&
                    row.pagamento?.gatewayPaymentId && (
                      <Tooltip title="Verificar status no Asaas">
                        <button
                          onClick={() => sincronizarInscricao(row.id, row.nome)}
                          disabled={sincronizando === row.id}
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#d97706',
                            background: '#fffbeb',
                            border: '1px solid #fde68a',
                            borderRadius: 99,
                            padding: '5px 12px',
                            cursor:
                              sincronizando === row.id ? 'wait' : 'pointer',
                            whiteSpace: 'nowrap',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            opacity: sincronizando === row.id ? 0.6 : 1,
                          }}
                        >
                          <RefreshCw size={12} />
                          Sincronizar
                        </button>
                      </Tooltip>
                    )}
                  {row.status === 'CONFIRMED' && (
                    <Tooltip title="Reenviar comprovante">
                      <button
                        onClick={() =>
                          enviarComprovante(row.id, row.nome, row.email)
                        }
                        disabled={enviando === row.id}
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#2563eb',
                          background: '#eff6ff',
                          border: '1px solid #bfdbfe',
                          borderRadius: 99,
                          padding: '5px 12px',
                          cursor: enviando === row.id ? 'wait' : 'pointer',
                          whiteSpace: 'nowrap',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          opacity: enviando === row.id ? 0.6 : 1,
                        }}
                      >
                        <Mail size={12} />
                        Comprovante
                      </button>
                    </Tooltip>
                  )}
                </div>
              ),
            },
          ]}
          options={{
            emptyMessage: 'Nenhuma inscrição neste evento ainda',
            cardGap: 12,
          }}
        />
      )}
    </AdminLayout>
  );
}
